import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { prop, propEq } from '@gov-nx/core/types';
import type { Tree } from '@gov-nx/ui/types';
import { useTree } from '@gov-nx/utils/common';

interface FormCheckboxesHookProps {
	tree: Tree;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formMethods: UseFormReturn<any>;
}

export type TreeFormCheckboxes = ReturnType<typeof useTreeFormCheckboxes>;

export const useTreeFormCheckboxes = ({
	formMethods,
	tree,
}: FormCheckboxesHookProps) => {
	const operations = useTree(tree);

	const isAnyFieldChecked = (): boolean =>
		!!getAllFields().find((name) => formMethods.watch(name) === true);

	const isAnyChildrenFieldChecked = () =>
		!!getChildrenFields().find((name) => formMethods.watch(name) === true);

	const sumChildren = (children: { name: string }[]): number =>
		children.reduce(
			(all, current) =>
				formMethods.watch(current.name) === true ? all + 1 : all,
			0
		);

	const getTree = (): Tree => ({
		...tree,
		children: tree.children.map((field) => ({
			...field,
			label: `${field.label} (${sumChildren(field.children)}/${
				field.children.length
			})`,
		})),
	});

	const getGroupField = (name: string): Tree['children'][number] | undefined =>
		tree.children.find(propEq('name', name));

	const getChildren = (name: string): string[] =>
		getGroupField(name)?.children.map(prop('name')) ?? [];

	const getAllFields = (): (Tree['name'] &
		Tree['children'][number]['name'] &
		Tree['children'][number]['children'][number]['name'])[] => [
		tree.name,
		...tree.children.reduce(
			(all, current) => [
				...all,
				current.name,
				...current.children.map(prop('name')),
			],
			[] as string[]
		),
	];

	const getGroupFieldOfChild = (name: string) =>
		tree.children.find((f) => f.children.some(propEq('name', name)));

	const getChildrenFieldsOfGroupField = (name: string) =>
		getGroupField(name)?.children || [];

	const getGroupFields = (): Tree['children'][number]['name'][] =>
		tree.children.reduce(
			(all, current) => [...all, current.name],
			[] as string[]
		);

	const getChildrenFields =
		(): Tree['children'][number]['children'][number]['name'][] =>
			tree.children.reduce(
				(all, current) => [...all, ...current.children.map(prop('name'))],
				[] as string[]
			);

	const change =
		(names: string[]) =>
		(isChecked: boolean): void => {
			names.forEach((name) => {
				const value = formMethods.getValues(name);
				if (value !== isChecked) {
					formMethods.setValue(name, isChecked);
				}
			});
		};

	const isRootField = (name: string) => name === tree.name;

	const isGroupField = (
		name: string
	): name is Tree['children'][number]['name'] => !!getGroupField(name);

	const isChildField = (name: string) =>
		!isRootField(name) && !isGroupField(name);

	const changeChildren = (name: string) => change(getChildren(name));
	const changeRootField = change(getGroupFields());

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const realField = (name: string, value: any) => {
		if (name.indexOf('.') > -1) {
			const [parent, fieldName] = name.split('.');
			return {
				isChecked: !!value[parent][fieldName],
			};
		}
		return {
			isChecked: !!value[name],
		};
	};

	useEffect(() => {
		const subscription = formMethods.watch((value, { name, type }) => {
			if (!name) return;

			const { isChecked } = realField(name, value);
			const isClicked = type === 'change';
			if (isRootField(name) && isClicked) {
				changeRootField(isChecked);
				if (isChecked) {
					operations.openNode(name);
				}
			}

			if (isGroupField(name)) {
				const gf = getGroupField(name);
				if (gf == null) {
					return;
				}
				const allChildrenChecked = gf.children.every((c) => value[c.name]);
				const atLeastOneChildUnchecked = gf.children.some(
					(c) => !value[c.name]
				);
				if (
					(!isChecked && allChildrenChecked) ||
					(isChecked && atLeastOneChildUnchecked)
				) {
					changeChildren(name)(isChecked);
				}
				if (isChecked) {
					operations.openNode(name);
				}
				if (!isClicked && !isChecked && value[tree.name]) {
					change([tree.name])(false);
				}
				if (
					!isClicked &&
					isChecked &&
					tree.children.every((c) => value[c.name])
				) {
					change([tree.name])(true);
				}

				if (isClicked) {
					if (value[tree.name]) {
						change([tree.name])(false);
					} else if (getGroupFields().every((f) => value[f])) {
						change([tree.name])(true);
					}
				}
			}

			if (isChildField(name) && isClicked) {
				const gf = getGroupFieldOfChild(name);
				if (gf == null) {
					return;
				}
				const gfChildren = getChildrenFieldsOfGroupField(gf.name);
				const allChildrenOfGroudFieldChecked = gfChildren.every(
					(c) => value[c.name]
				);
				const allOtherSiblingsChecked = gfChildren
					.filter((c) => c.name !== name)
					.every((c) => value[c.name]);

				if (isChecked && allChildrenOfGroudFieldChecked) {
					change([gf.name])(true);
				} else if (!isChecked && allOtherSiblingsChecked) {
					change([gf.name])(false);
				}
			}
		});

		return () => subscription.unsubscribe();
	}, [
		formMethods.watch,
		formMethods,
		operations,
		changeChildren,
		changeRootField,
		isGroupField,
		isRootField,
	]);

	const getDescendantNames = (name: string): string[] => {
		if (isRootField(name)) {
			return [
				...tree.children.map((g) => g.name),
				...tree.children.flatMap((g) => g.children.map((ch) => ch.name)),
			];
		}
		if (isGroupField(name)) {
			return getChildren(name);
		}
		return [];
	};

	const hasPartiallyCheckedDescendants = (name: string) => {
		const values = formMethods.getValues();
		const descendants = getDescendantNames(name);
		if (descendants.length === 0) return false;

		const checkedCount = descendants.filter(
			(n) => realField(n, values).isChecked
		).length;

		return checkedCount > 0 && checkedCount < descendants.length;
	};

	return {
		operations,
		getTree,
		reset: operations.reset,
		isAnyFieldChecked,
		hasPartiallyCheckedDescendants,
		isAnyChildrenFieldChecked,
		isRootField,
		isGroupField,
	};
};
